// Typesafe API URL registry — shared shapes.
//
// A registry is a nested tree of `RouteEntry` leaves grouped by resource, e.g.
// `{ auctions: { list: <entry>, detail: <entry> }, invoices: { ... } }`. The
// nesting path doubles as the route's stable dotted key (`auctions.detail`) used
// by the parity test and dead-route report. Path + params + verb are typed from
// the registry; response bodies are typed by an opt-in `responseType<DTO>()`
// marker on the verb (ADR 027 / BL-855) that the client threads into the call's
// return — so callers infer the response instead of asserting a per-call `<T>`
// generic (which defaults to `unknown` when no marker is present). Nothing here
// imports `node:*` or `server-only` — the file is client-bundle-safe so
// hooks/components can import the registry + client.

/** HTTP verbs a route file may export. Mirrors the wrap-coverage crawler's set. */
export type HttpVerb = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'OPTIONS' | 'HEAD';

/**
 * Who is the EXPECTED caller of a route. Drives the dead-route report: only
 * `'internal'` routes (called by this app's own client) are flagged when they
 * have zero `apiClient` references. Everything else is supposed to have no
 * internal caller and is excluded from the report.
 *
 * - `internal` — called by this app's frontend via `apiClient` (default).
 * - `webhook`  — provider-to-server callback (Finix/payment webhooks).
 * - `cron`     — Vercel Cron route (`/api/cron/*`), invoked by the scheduler.
 * - `external` — third-party auth dispatch (e.g. `/api/auth/[...all]`).
 * - `public`   — `/api/public/*`, consumed by unauthenticated / cross-app surfaces.
 * - `mobile`   — consumed by `apps/admin-catalog/` over HTTP (no web caller).
 * - `realtime` — reached via the realtime snapshot catch-up dispatcher
 *   (`/api/realtime/${kind}/catchup` in `snapshot-store.ts`), which builds the
 *   URL from a runtime topic-kind, so no direct typed-client key references it.
 */
export type CallerKind =
  | 'internal'
  | 'webhook'
  | 'cron'
  | 'external'
  | 'public'
  | 'realtime';

/**
 * Phantom type carrier for a verb's response DTO (ADR 027 / BL-855). Holds the
 * response type `T` purely at the type level — the runtime value is inert. The
 * client extracts `T` and threads it into the call's return, so callers infer
 * the response instead of asserting it with a per-call `<T>` generic.
 *
 * @see responseType — the authoring helper that produces one.
 */
declare const RESPONSE_BRAND: unique symbol;
export interface ResponseMarker<T> {
  readonly [RESPONSE_BRAND]: T;
}

/**
 * Author a route's response type: `methods: { GET: { response: responseType<LotDetail>() } }`.
 * Returns an inert value branded with `T`; nothing reads it at runtime. `T` is a
 * `Pick<Row>` composition over `$inferSelect` (ADR 027) — a single row, or an
 * intersection of rows plus computed fields. The client infers `T` as the
 * `get()`/`post()` result, so the caller drops its `<T>` generic and its
 * re-declared response interface.
 */
export function responseType<T>(): ResponseMarker<T> {
  return {} as ResponseMarker<T>;
}

/**
 * Per-verb metadata. All fields are opt-in and additive.
 *
 * `R` is the response DTO carried by {@link responseType}. It defaults to
 * `unknown`, so an entry with no `response` marker behaves exactly as before
 * (the caller's `<T>` generic, defaulting to `unknown`, types the result).
 */
export interface VerbSpec<R = unknown> {
  /**
   * Opt-in response DTO carrier (ADR 027). When present, the client returns
   * `ApiEnvelope<R>` for this verb and the caller needs no `<T>` generic.
   */
  response?: ResponseMarker<R>;
  /** Opt-in, additive query-param surface for this verb. Loose by design. */
  query?: QueryShape;
}

/** Extract the response DTO carried by a {@link VerbSpec}, or `unknown` if none. */
export type InferResponse<S> = S extends VerbSpec<infer R> ? R : unknown;

/** Query params accepted by a verb. Values are coerced to strings at build time. */
export type QueryShape = Record<string, string | number | boolean | undefined>;

/** A single route's contract: its URL template, path params, and allowed verbs. */
export interface RouteEntry<
  P extends Record<string, string> = Record<string, never>,
  M extends Partial<Record<HttpVerb, VerbSpec>> = Partial<Record<HttpVerb, VerbSpec>>,
> {
  /**
   * URL template with `:param` placeholders, e.g.
   * `/api/invoices/:id/payments/:txId`. A catch-all segment is written `:name*`
   * (only `bidder /api/auth/:all*` exists today). Must equal the path the parity
   * crawler derives from the route folder — that equality is the sync guarantee.
   */
  path: string;
  /**
   * Path-param names mapped to `string`. Author with `''` placeholders so the
   * generic `P` is inferred from the literal: `params: { id: '', txId: '' }`.
   * Static routes use `{}`.
   */
  params: P;
  /**
   * Verbs the route file exports. Parity asserts this set === the file's exports.
   * Captured as the generic `M` so the client exposes exactly these verbs (not a
   * widened all-optional set).
   */
  methods: M;
  /** Expected caller. Defaults to `'internal'`. See {@link CallerKind}. */
  caller?: CallerKind;
  /** Internal brand so `createRegistry`/`createApiClient` can tell leaves from subtrees. */
  readonly __routeEntry?: true;
}

/** A registry tree node: either a route leaf or a nested group of nodes. */
export type RegistryNode = RouteEntry<Record<string, string>> | RegistryTree;

/** A nested group of registry nodes. The top-level registry is a `RegistryTree`. */
export interface RegistryTree {
  [key: string]: RegistryNode;
}

/** A flattened view of one entry, carrying its derived dotted key + URL. */
export interface FlatEntry {
  /** Dotted nesting path, e.g. `auctions.detail`. Stable id for parity + reports. */
  key: string;
  /** The route's `path` template. */
  path: string;
  /** Declared verbs. */
  methods: HttpVerb[];
  /** Expected caller (defaults applied). */
  caller: CallerKind;
}
